import { useMemo } from "react";
import {
  buildPayload,
  newProgramToProgram,
  programTargetProjection,
  type Draft,
  type Exercise,
  type TargetItem,
  type UnitsPreference,
} from "@jackedlogs/shared";
import { useLocale } from "../primitives/useLocale";

// ME-3 — the read-only day×week projection of the ATHLETE's targets across
// the program being edited. Extracted from ProgramComposer when the unit
// threading pushed that file over the 500-line cap; it was already one
// self-contained concern (the composer only ever reads the final
// `projection` out of it), so this is a seam, not a split for the count.
//
// Nothing here writes: it serializes the draft ONLY to score it.
export function useTargetProjection({
  draft,
  cellUnit,
  exerciseCatalog,
  maxes,
  athleteTargets,
}: {
  draft: Draft;
  // The unit the draft's absolute cells are in — NOT the live preference.
  // Reading lb figures as kg would inflate this projection's tonnage and
  // %-of-max by 2.2 without anything looking wrong.
  cellUnit: UnitsPreference;
  exerciseCatalog: ReadonlyArray<Exercise> | undefined;
  maxes: Map<string, number> | null;
  // Nullable: the host passes the fetched targets doc, which is null while
  // it loads and absent when the athlete has none.
  athleteTargets?: { items: TargetItem[] } | null | undefined;
}) {
  // The draft serialized through the SAME save-path serializers
  // (buildPayload → newProgramToProgram), so the readout scores the program
  // that would actually be saved. null on an invalid draft (mid-typing).
  const builtProgram = useMemo(() => {
    const built = buildPayload(draft, cellUnit);
    return built.ok ? newProgramToProgram(built.payload) : null;
  }, [draft, cellUnit]);

  const athleteTargetItems = useMemo(
    () => athleteTargets?.items ?? [],
    [athleteTargets],
  );

  // null while the draft is invalid, the catalog hasn't loaded, or the
  // athlete has no scoreable targets — the section simply doesn't render.
  const lang = useLocale();
  return useMemo(() => {
    if (!exerciseCatalog || !builtProgram || athleteTargetItems.length === 0)
      return null;
    return programTargetProjection(
      builtProgram,
      exerciseCatalog,
      maxes,
      athleteTargetItems,
      lang,
    );
  }, [builtProgram, exerciseCatalog, maxes, athleteTargetItems, lang]);
}
